
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { getFeaturedPost } from '../../../mocks/blogPosts';

interface FeaturedPostProps {
  searchQuery: string;
}

export default function FeaturedPost({ searchQuery }: FeaturedPostProps) {
  const { ref, isVisible } = useScrollAnimation();
  const featuredPost = getFeaturedPost();

  if (!featuredPost) return null;

  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    const matches =
      featuredPost.title.toLowerCase().includes(q) ||
      featuredPost.excerpt.toLowerCase().includes(q) ||
      featuredPost.category.toLowerCase().includes(q) ||
      featuredPost.author.toLowerCase().includes(q);
    if (!matches) return null;
  }

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <Link to={`/blog/${featuredPost.slug}`} className="relative group cursor-pointer">
            <div className="absolute -inset-4 bg-gradient-to-r from-[#00989e]/20 to-[#00b4a6]/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative overflow-hidden rounded-2xl shadow-xl">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                className="w-full h-80 lg:h-96 object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1.5 bg-[#00989e] text-white text-xs font-bold rounded-full">
                  FEATURED
                </span>
              </div>
            </div>
          </Link>

          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-[#00989e]/10 text-[#00989e] text-xs font-semibold rounded-full">
                {featuredPost.category}
              </span>
              <span className="text-slate-400 text-sm">{featuredPost.readTime}</span>
            </div>

            <Link to={`/blog/${featuredPost.slug}`} className="block cursor-pointer">
              <h2 className="font-montserrat text-3xl lg:text-4xl font-bold text-slate-800 leading-tight hover:text-[#00989e] transition-colors">
                {featuredPost.title}
              </h2>
            </Link>

            <p className="text-slate-600 text-lg leading-relaxed">
              {featuredPost.excerpt}
            </p>

            <div className="flex items-center gap-4 pt-2">
              <img
                src={featuredPost.authorImage}
                alt={featuredPost.author}
                className="w-12 h-12 rounded-full object-cover object-top"
              />
              <div>
                <p className="font-semibold text-slate-800">{featuredPost.author}</p>
                <p className="text-sm text-slate-500">{featuredPost.authorRole} • {featuredPost.date}</p>
              </div>
            </div>

            <Link
              to={`/blog/${featuredPost.slug}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap group"
            >
              Read Full Article
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
